import { Injectable, Input } from '@angular/core';
import { LBatchSigning } from 'core/config';
import { EStatus, ISignatureConfiguration } from 'core/dto';
import { IPageInfo, PdfDocumentViewportsContainerService, PdfViewerService } from 'core/pdf-new';
import { filterAllTruthyInTuple, subjectFromObs } from 'core/rx-js';
import { ScreenUtilsService } from 'core/services';
import { DsgDeepTranslationKey } from 'projects/designer/src/app/pipes/translate.pipe';
import { BehaviorSubject, Subject, combineLatest, debounceTime, distinctUntilChanged, filter, map, startWith, switchMap } from 'rxjs';
import { IActivity } from '../../../../dto-map/i-activity';
import { ITask } from '../../../../dto-map/i-task';
import { RemoteActivityService } from '../../../../remotes/remote-activity.service';
import { SelectTasksStepService } from '../../select-tasks-step.service';
import { TaskCollectionService } from '../../task-collection.service';

@Injectable()
export class ActorCardItemService {
  readonly #batchSigningEnumDict: Record<LBatchSigning, DsgDeepTranslationKey> = {
    none: 'ss-entities.enum-batch-signing.none',
    all: 'ss-entities.enum-batch-signing.all',
    custom: 'ss-entities.enum-batch-signing.custom'
  }

  private readonly _destroy = new Subject<void>();
  public get destroy() {
    return this._destroy;
  }
  readonly hangingSignaturePositionSubject = this._selectTasksStepService.hangingSignaturePositionSubject;
  readonly activitySubject = new BehaviorSubject<IActivity>(null)
  @Input() get activity() {
    return this.activitySubject.value
  }
  set activity(value) {
    this.activitySubject.next(value)
  }

  readonly backgroundColorObs = this.activitySubject.pipe(
    filter(Boolean),
    map(activity => 'hsla(' + activity?.actor?.hue + ',75%,50%,0.3'),
    distinctUntilChanged()
  )

  readonly showSettingsSubject = new BehaviorSubject<boolean>(false);
  get #showSettings() {
    return this.showSettingsSubject.value
  }
  readonly tasksSubject = this._taskCollectionService.tasksSubject

  readonly selectedDocumentObs = this._viewerService.selectedDocumentObs;
  readonly selectedDocumentSubject = subjectFromObs({ observable: this.selectedDocumentObs, until: this._destroy, initialValue: undefined })

  readonly inDragSubject = new BehaviorSubject<boolean>(false);

  readonly virtualScrollableElementSubject = this._pdfDocumentViewportsContainerService.virtualScrollableElementSubject;
  readonly scrollOffsetObs = this.virtualScrollableElementSubject
    .pipe(
      filter(Boolean),
      switchMap((virtualScrollableElement) =>
        virtualScrollableElement.elementScrolled()
          .pipe(
            debounceTime(200),
            map(() => virtualScrollableElement.measureScrollOffset('top'))
          )
      ),
      startWith(0)
    )

  readonly scrollOffsetSubject = subjectFromObs({ observable: this.scrollOffsetObs, until: this._destroy, initialValue: 0 })

  readonly dragStyleObs = combineLatest([this.inDragSubject, this.backgroundColorObs, this.hangingSignaturePositionSubject]).pipe(
    map(([inDrag, background, hangingSignaturePosition]) => ({
      'background-color': inDrag ? background : null,
      position: inDrag ? 'relative' : 'absolute',
      transform: inDrag ? 'translate3d(' + hangingSignaturePosition.x + 'px,' + hangingSignaturePosition.y + 'px,0px)' : null
    }))
  )


  get #signatures() {
    if (!this._taskCollectionService.signatures) {
      return []
    }
    return this._taskCollectionService.signatures as ITask<'SIGNATURE'>[]
  }


  readonly isSmallerScreenObs = this._screenUtilsService.screenSizeSubject.pipe(
    map(screenSize => !['xl', 'lg'].includes(screenSize))
  )

  readonly batchSigningSubject = subjectFromObs({
    initialValue: null,
    observable: combineLatest([this._remoteActivityService.activityBatchSigningSubject, this.activitySubject]).pipe(
      filterAllTruthyInTuple(),
      map(([activityBatchSigning, activity]) => activityBatchSigning[activity.id])
    ),
    until: this._destroy
  })

  get #batchSigning() {
    return this.batchSigningSubject.value
  }

  readonly batchSigningDataSource = Object.entries(this.#batchSigningEnumDict).map(([key, display]) => ({ value: key, display }))


  readonly activityTasksObs = combineLatest([this.tasksSubject, this.activitySubject]).pipe(
    filterAllTruthyInTuple(),
    map(([tasks, activity]) => tasks.filter(task => task.activityId === activity.id)),
  )



  constructor(
    private readonly _taskCollectionService: TaskCollectionService,
    private readonly _viewerService: PdfViewerService,
    private readonly _pdfDocumentViewportsContainerService: PdfDocumentViewportsContainerService,
    private readonly _selectTasksStepService: SelectTasksStepService,
    private readonly _screenUtilsService: ScreenUtilsService,
    private readonly _remoteActivityService: RemoteActivityService

  ) {


  }



  readonly TOLARATED_OFFSET = 35;

  #isPositionValid(position: { top: number, left: number }, pageInfo: IPageInfo) {


    return !this.#signatures
      .filter((signature) => signature.documentId === pageInfo.docId && signature.configuration.signaturePosition.pageNumber === pageInfo.realPageIdx - 1)
      .find(({ configuration: { signaturePosition } }) => {

        return Math.abs(signaturePosition.top - position.top) < this.TOLARATED_OFFSET
          && Math.abs(signaturePosition.left - position.left) < this.TOLARATED_OFFSET;
      })
  }

  #getNonConflictingPosition(signatureTop: number, signatureLeft: number, selectedPageInfo: IPageInfo, height: number, width: number) {
    let possibleTop = signatureLeft;
    let possibleLeft = signatureLeft;

    const maxTop = selectedPageInfo.dimensions.height - height;
    const maxLeft = selectedPageInfo.dimensions.width - width

    while (possibleTop < maxTop || possibleLeft < maxLeft) {

      const isValid = this.#isPositionValid({ left: possibleLeft, top: possibleTop }, selectedPageInfo);

      if (isValid) {
        return { top: possibleTop, left: possibleLeft };
      }

      possibleTop = Math.min(possibleTop + this.TOLARATED_OFFSET, maxTop);
      possibleLeft = Math.min(possibleLeft + this.TOLARATED_OFFSET, maxLeft);
    }

    possibleTop = signatureTop;
    possibleLeft = signatureLeft;
    const minTop = 0;
    const minLeft = 0;

    while (possibleTop > minTop || possibleLeft > minLeft) {
      const isValid = this.#isPositionValid({ left: possibleLeft, top: possibleTop }, selectedPageInfo);

      if (isValid) {
        return { top: possibleTop, left: possibleLeft };
      }

      possibleTop = Math.min(possibleTop - this.TOLARATED_OFFSET, maxTop);
      possibleLeft = Math.min(possibleLeft - this.TOLARATED_OFFSET, maxLeft);
    }


    return { top: signatureTop, left: signatureLeft }
  }



  dropSignature() {
    const DEFAULT_HEIGHT = 100;
    const DEFAULT_WIDTH = 200;
    const selectedPageInfo = this._viewerService.selectedPageInfo;


    const { realPageIdx, docId } = selectedPageInfo
    const defaultTop = (this._viewerService.scrollTopSubject.value + 300) - (selectedPageInfo.scrollTop);

    const allowedSignatureTypes = this._taskCollectionService.getActivityConfiguredSignatureTypes(this.activity);

    const defaultLeft = selectedPageInfo.dimensions.width / 2 - DEFAULT_HEIGHT

    const { top, left } = this.#getNonConflictingPosition(defaultTop, defaultLeft, selectedPageInfo, DEFAULT_HEIGHT, DEFAULT_WIDTH)


    const insertedSignatureConfig: ISignatureConfiguration = {
      allowedSignatureTypes,
      signaturePosition: {
        height: DEFAULT_HEIGHT,
        width: DEFAULT_WIDTH,
        left: left || defaultLeft,
        top: top || defaultTop,
        pageNumber: realPageIdx - 1,
      },
      pades: {
        baselineLevel: null
      },
      signatureVisible: true
    }

    this._taskCollectionService
      .addTask({
        type: 'SIGNATURE',
        batchName: this.#batchSigning === 'all' ? '*' : '',
        activityId: this.activity.id,
        documentId: docId,
        orderIndex: 0,
        status: EStatus.Draft,
        configuration: insertedSignatureConfig,
      })

  }


  nextSignature() {

    this._taskCollectionService.nextSignature(this.activity.id)
  }

  prevSignature() {

    this._taskCollectionService.prevSignature(this.activity.id)
  }

  toggleShowSettings() {
    this.showSettingsSubject.next(!this.#showSettings)
  }

  setActivityBatchSigning(batchSigning: LBatchSigning) {
    this._remoteActivityService.setActivityBatchSigning(this.activity.id, batchSigning);
  }

}
